import React from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    FormHelperText,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import AppDialogTitle from '../../ui/appDialogTitle';
import ProvinceSelect from '../../common/provinceSelect';
import CitySelect from '../../common/citySelect';
import { ACCEPTED_IMAGE_FILE_FORMAT, formatFileSize } from '../../../utils';
import AttachmentUploader from '../../common/attachmentUploader';
import LocationPreferences from './LocationPreferences';
import { LocationService } from '../../../service/locationService';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../utils/environment';
import { extendedLocationSchema } from './locationPreferencesValidation';
import { TIMEZONE_OPTIONS } from '../../../utils/timezones';

const MAX_FILE_SIZE = 1 * 1024 * 1024
const MAX_NO_OF_FILES = 1

// Initial values for the location form
// Added preferences object to handle location-specific settings
const initialValues = {
    addrNo: '',
    addrUnit: '',
    addrStreet: '',
    addrCity: '',
    addrProvince: '',
    addrPostal: '',
    //addrCountry: 'Canada',
    website: '',
    googleReviewLink: '',
    name: '',
    storePrefix: '',
    phone: '',
    fax: '',
    email: '',
    timezone: 'America/New_York', // Default timezone
    status: '',
    logo: '',
    // Location preferences - will inherit from business defaults initially
    preferences: {
        phoneFormat: '',
        countryCode: '',
        timezone: '',
        currency: '',
        dateFormat: '',
        postalCodeFormat: '',
        invoiceFormat: '',
        prescriptionFormat: ''
    }
};

export default function LocationInputForm({ open, onClose, onLocationAdded }) {

    /**
     * Handle form submission
     * Processes both basic location data and preferences
     * @param {Object} values - Form values including location details and preferences
     * @param {Object} formikBag - Formik helper functions
     */
    const handleSubmit = (values, { resetForm, isSubmitting, setSubmitting }) => {
        // Prepare the payload by combining location data with preferences
        const locationData = {
            ...values,
            // Only include preferences that have been customized (non-empty values)
            preferences: Object.keys(values.preferences).reduce((acc, key) => {
                if (values.preferences[key] && values.preferences[key].trim() !== '') {
                    acc[key] = values.preferences[key];
                }
                return acc;
            }, {})
        };

        LocationService.addLocation(locationData).then(response => {
            const { code, data } = response;
            if (code === 0) {
                toast.success("Location and preferences saved successfully");
                resetForm();
                onLocationAdded(data);
            }
        }).catch(error => {
            if (isLocalDev) {
                console.log(error);
                setSubmitting(false);
            }

            toast.error('Failed to add location');
        });
    };

    return (
        <Dialog open={open} maxWidth={'md'}>
            <AppDialogTitle title={"Add Location"} onClose={onClose} />
            <DialogContent>
                {/* <form onSubmit={formik.handleSubmit}> */}
                <Formik
                    initialValues={initialValues}
                    validationSchema={extendedLocationSchema}
                    onSubmit={handleSubmit}
                >
                    {({form, field, values, setFieldValue, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} mt={1}>
                                    <Field
                                        as={TextField}
                                        name="name"
                                        label="Name"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <FormHelperText>Choose a friendly name to help you choose the location across the app.</FormHelperText>
                                    <ErrorMessage name="name">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6} mt={1}>
                                    <Field
                                        as={TextField}
                                        name="storePrefix"
                                        label="Store prefix"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <FormHelperText>Used when generating invoices e.g. Dundas use 'DUN' or 'D'</FormHelperText>
                                    <ErrorMessage name="storePrefix">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrNo"
                                        label="Number"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Field
                                        as={TextField}
                                        name="addrStreet"
                                        label="Street"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrUnit"
                                        label="Unit"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        name="addrCity"
                                        component={CitySelect}
                                        countryId={1} // Pass the countryId as a prop
                                        label="City"
                                    />
                                    <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        name="addrProvince"
                                        component={ProvinceSelect}
                                        countryId={1} // Pass the countryId as a prop
                                        label="Province"
                                    />
                                    {/* <Field
                                        as={TextField}
                                        name="addrProvince"
                                        label="Province"
                                        required
                                        fullWidth
                                    /> */}
                                    <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Field
                                        as={TextField}
                                        name="addrPostal"
                                        label="Postal code"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="phone"
                                        label="Phone"
                                        size="small"
                                        required
                                        fullWidth
                                    />
                                    <ErrorMessage name="phone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name="fax"
                                        label="Fax"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="fax">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="email"
                                        label="Email"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>Timezone</InputLabel>
                                        <Field name="timezone">
                                            {({ field, form }) => (
                                                <Select
                                                    {...field}
                                                    label="Timezone"
                                                    onChange={(e) => form.setFieldValue('timezone', e.target.value)}
                                                >
                                                    {TIMEZONE_OPTIONS.map((tz) => (
                                                        <MenuItem key={tz.value} value={tz.value}>
                                                            {tz.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </Field>
                                    </FormControl>
                                    <ErrorMessage name="timezone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="website"
                                        label="Website"
                                        size="small"
                                        fullWidth
                                    />
                                    <ErrorMessage name="website">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="googleReviewLink"
                                        label="Review link"
                                        size="small"
                                        fullWidth
                                    />
                                    <FormHelperText>A link you can send in email or text for customers to leave a review.</FormHelperText>
                                    <ErrorMessage name="googleReviewLink">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant='caption'>Logo (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)}). Max files allowed {MAX_NO_OF_FILES}</Typography>
                                    <Field
                                        name="logo"
                                        values={values.logo}
                                        component={AttachmentUploader}
                                        onUpload={(files) => setFieldValue('logo', files[0])}
                                        accept={
                                            ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                                acc[mimeType] = []
                                                return acc
                                            }, {})
                                        }
                                        maxFiles={MAX_NO_OF_FILES}
                                        maxSize={MAX_FILE_SIZE}
                                    />
                                </Grid>

                                {/* Location Preferences Section */}
                                <Grid item xs={12}>
                                    <LocationPreferences 
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        // businessPreferences prop would come from context/API in real implementation
                                        // For now, component uses internal defaults
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                        sx={{ mt: 2 }}
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create Location'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
                {/* </form> */}
            </DialogContent>
        </Dialog>
    );
};